import * as React from "react";
import styled from "@emotion/styled";
import {
  Button,
  Checkbox,
  FadeIn,
  IconChevron,
  Input,
  Label,
  Radio,
} from "atoms";
import { getPreference, setPreference, deletePreference } from "utils/storage";
import {
  badges,
  blockchains,
  doxxed,
  founderAttributes,
  artistAttributes,
  mintStatuses,
  missionOrCauses,
  roadmapCategories,
} from "features/reviewable/data";
import { fieldNames } from "../utils/filterFieldNames";
import { resetFilters } from "../utils/resetFilters";

/**
 * Validation errors mapped by form field name
 */
type FilterErrors = Partial<typeof fieldNames>;

interface SearchFiltersProps {
  errors: FilterErrors | null;
  formId: string;
  formRef: React.RefObject<HTMLFormElement>;
  isLoading: boolean;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  breakpoint: string;
}

interface FilterDropdownProps {
  children: React.ReactNode;
  legend: string;
  withScroll?: boolean;
}

function FilterDropdown({
  children,
  legend,
  withScroll = false,
}: FilterDropdownProps) {
  return (
    <Details withScroll={withScroll}>
      <Summary>
        {legend}
        <IconChevron />
      </Summary>

      <Fieldset withScroll={withScroll}>{children}</Fieldset>
    </Details>
  );
}

function SearchFilters({
  errors,
  formId,
  formRef,
  isLoading,
  onSubmit,
  breakpoint,
  ...props
}: SearchFiltersProps) {
  // Combine Artist + Founder Tags, Elimate Duplicates, Sort
  const allFounderAttributes = React.useMemo(
    () => [...new Set([...founderAttributes, ...artistAttributes])].sort(),
    []
  );

  // Check user preferences for showing advanced filters
  const [isAdvanced, setIsAdvanced] = React.useState(
    !!getPreference("SHOW_ADVANCED_FILTERS")
  );

  // Toggle showing/hiding advanced filters
  const toggleAdvanced = () => {
    // Update user preferences first
    if (isAdvanced) {
      deletePreference("SHOW_ADVANCED_FILTERS");
    } else {
      setPreference("SHOW_ADVANCED_FILTERS", "true");
    }
    setIsAdvanced(!isAdvanced);
  };

  // Set all form inputs to empty or unchecked values
  const resetFiltersHandler = () => {
    if (formRef.current) resetFilters(formRef.current);
  };

  return (
    <form onSubmit={onSubmit} ref={formRef} id={formId} {...props}>
      <FilterDropdown legend="Type">
        {/* NOTE: radio buttons can't be de-selected, so a default option with no value is needed */}
        <SRadio name="type" label="All" value="" defaultChecked />
        {["project", "artist"].map((type) => (
          <SRadio
            name="type"
            label={`${type[0].toUpperCase()}${type.slice(1)}`}
            value={type}
            key={type}
          />
        ))}
      </FilterDropdown>

      <FilterDropdown legend="Founder / Artist" withScroll>
        {allFounderAttributes.map((attr) => (
          <SCheckbox
            name={fieldNames.founderAttributes}
            label={attr}
            value={attr}
            key={attr}
          />
        ))}
      </FilterDropdown>

      <FilterDropdown legend="Mission or Cause" withScroll>
        {missionOrCauses.map((mission) => (
          <SCheckbox
            name={fieldNames.missionOrCause}
            label={mission}
            value={mission}
            key={mission}
          />
        ))}
      </FilterDropdown>

      <FilterDropdown legend="Blockchain" withScroll>
        {blockchains.map((chain) => (
          <SCheckbox
            name={fieldNames.blockchain}
            label={chain}
            value={chain}
            key={chain}
          />
        ))}
      </FilterDropdown>

      {isAdvanced && (
        <FadeIn>
          <FilterDropdown legend="Roadmap Categories">
            {roadmapCategories.map((status) => (
              <SCheckbox
                name={fieldNames.roadmapCategories}
                label={status}
                value={status}
                key={status}
              />
            ))}
          </FilterDropdown>

          <FilterDropdown legend="Badges">
            {badges.map((status) => (
              <SCheckbox
                name={fieldNames.badges}
                label={status}
                value={status}
                key={status}
              />
            ))}
          </FilterDropdown>

          <FilterDropdown legend="Mint Status">
            {mintStatuses.map((status) => (
              <SCheckbox
                name={fieldNames.mintStatus}
                label={status}
                value={status}
                key={status}
              />
            ))}
          </FilterDropdown>

          <FilterDropdown legend="Doxxed">
            {doxxed.map((dox) => (
              <SCheckbox
                name={fieldNames.doxxed}
                label={dox}
                value={dox}
                key={dox}
              />
            ))}
          </FilterDropdown>

          <FilterDropdown legend="Collection Size">
            <RangeInputs breakpoint={breakpoint}>
              <SLabel>
                Min collection size
                <SInput
                  type="number"
                  name={fieldNames.collectionSizeGte}
                  placeholder="Min"
                  isInvalid={!!errors?.collectionSizeGte}
                  step="1"
                  min="0"
                />
              </SLabel>

              <SLabel>
                Max collection size
                <SInput
                  type="number"
                  name={fieldNames.collectionSizeLte}
                  placeholder="Max"
                  isInvalid={!!errors?.collectionSizeLte}
                  step="1"
                  min="1"
                />
              </SLabel>
            </RangeInputs>
          </FilterDropdown>

          <FilterDropdown legend="Mint Price">
            <RangeInputs breakpoint={breakpoint}>
              <SLabel>
                Min mint price
                <SInput
                  type="number"
                  name={fieldNames.mintPriceGte}
                  placeholder="Min"
                  isInvalid={!!errors?.mintPriceGte}
                  step="any"
                  min="0"
                />
              </SLabel>

              <SLabel>
                Max mint price
                <SInput
                  type="number"
                  name={fieldNames.mintPriceLte}
                  placeholder="Max"
                  isInvalid={!!errors?.mintPriceLte}
                  step="any"
                  min="0"
                />
              </SLabel>
            </RangeInputs>
          </FilterDropdown>
        </FadeIn>
      )}

      <FilterDropdown legend="Floor Price">
        <RangeInputs breakpoint={breakpoint}>
          <SLabel>
            Min floor price
            <SInput
              type="number"
              name={fieldNames.floorPriceGte}
              placeholder="Min"
              isInvalid={!!errors?.floorPriceGte}
              step="any"
              min="0"
            />
          </SLabel>

          <SLabel>
            Max floor price
            <SInput
              type="number"
              name={fieldNames.floorPriceLte}
              placeholder="Max"
              isInvalid={!!errors?.floorPriceLte}
              step="any"
              min="0"
            />
          </SLabel>
        </RangeInputs>
      </FilterDropdown>

      {isAdvanced && (
        <FadeIn>
          <FilterDropdown legend="Mint Date">
            <RangeInputs breakpoint={breakpoint}>
              <SLabel>
                Earliest mint date
                <SInput
                  type="date"
                  name={fieldNames.mintDateGte}
                  isInvalid={!!errors?.mintDateGte}
                />
              </SLabel>

              <SLabel>
                Latest mint date
                <SInput
                  type="date"
                  name="mintDateLte"
                  isInvalid={!!errors?.mintDateLte}
                />
              </SLabel>
            </RangeInputs>
          </FilterDropdown>
        </FadeIn>
      )}

      <ButtonGroup>
        <PlainButton onClick={toggleAdvanced}>
          {isAdvanced ? "Hide" : "Show"} advanced filters
        </PlainButton>

        <Button type="submit" disabled={isLoading} variant="gradient" size="sm">
          Search
        </Button>

        <PlainButton onClick={resetFiltersHandler}>
          Clear selected filters
        </PlainButton>
      </ButtonGroup>

      {errors && (
        <ErrorList role="alert">
          {Object.entries(errors).map(([key, val]) => (
            <li key={key}>{val}</li>
          ))}
        </ErrorList>
      )}
    </form>
  );
}

type WithScroll = { withScroll: boolean };

const Details = styled.details<WithScroll>(({ theme, withScroll }) => ({
  borderBottom: `1px solid ${theme.colors.gray20}`,
  position: "relative",
  "&:first-of-type": {
    borderTop: `1px solid ${theme.colors.gray20}`,
  },

  ...(withScroll
    ? {
        "&:after": {
          background: `linear-gradient(rgba(255,255,255,0), ${theme.colors.bgLight})`,
          bottom: "0.5rem",
          content: "''",
          height: "2rem",
          left: 0,
          opacity: 0,
          pointerEvents: "none",
          position: "absolute",
          right: ".5rem",
          transition: "opacity 0.1s ease",
        },
        "&[open]:after": {
          opacity: 1,
        },
      }
    : {}),
}));

const Summary = styled.summary(({ theme }) => ({
  ...theme.fonts.body,
  alignItems: "center",
  borderRadius: theme.borderRadius.sm,
  cursor: "pointer",
  display: "flex",
  fontSize: theme.fontSizes.xs,
  justifyContent: "space-between",
  padding: "0.8rem 0.5rem",
  position: "relative",
  transition: "color ease 0.15s",
  userSelect: "none",
  zIndex: 1, // To sit on top of Details::after scroll gradient
  "&:hover": {
    color: theme.colors.primary,
  },
  "&:focus-visible": {
    outlineOffset: -2,
    outlineColor: theme.colors.fg,
  },
  svg: {
    transform: "rotate(180deg)",
    width: ".6em",
  },
  "[open] & svg": {
    transform: "rotate(0deg)",
  },
  // Attempt to hide default arrow
  listStyleType: "none",
  "&::-webkit-details-marker": {
    display: "none",
  },
}));

const Fieldset = styled.fieldset<WithScroll>(({ withScroll }) => ({
  border: 0,
  margin: "0 0 .5rem",
  ...(withScroll
    ? {
        height: "fit-content",
        maxHeight: "15rem",
        padding: "1rem .5rem 2rem", // Bottom same size as Details::after so it doesn't cover last element
        overflowY: "auto",
      }
    : {
        padding: "1rem .5rem",
      }),
}));

const SCheckbox = styled(Checkbox)(({ theme }) => ({
  alignItems: "center",
  fontSize: theme.fontSizes.xxs,
  lineHeight: "1.2",
}));

const SRadio = styled(Radio)(({ theme }) => ({
  alignItems: "center",
  fontSize: theme.fontSizes.xxs,
  lineHeight: "1.2",
}));

const ButtonGroup = styled.div({
  marginTop: "1.25rem",
  "button + button": {
    display: "block",
    marginTop: "1rem",
  },
  "button:last-of-type": {
    marginTop: "0.5rem",
  },
});

const PlainButton = styled(Button)(({ theme }) => ({
  fontSize: theme.fontSizes.xxs,
  padding: "0.2rem 0",
}));
PlainButton.defaultProps = {
  variant: "blank",
};

type WithBreakpoint = { breakpoint: string };

const RangeInputs = styled.div<WithBreakpoint>(({ breakpoint }) => ({
  alignItems: "baseline",
  display: "flex",
  gap: "1rem",
  "& > *": {
    margin: 0,
  },
  "& > span": {
    margin: 0,
  },
  [breakpoint]: {
    alignItems: "center",
    flexDirection: "column",
  },
}));

const SLabel = styled(Label)(({ theme }) => ({
  ...theme.fonts.body,
  fontSize: theme.fontSizes.xs,
  width: "100%",
  color: "rgba(0,0,0,.666)",
}));
const SInput = styled(Input)(({ theme }) => ({
  fontSize: theme.fontSizes.xs,
  margin: 0,
}));

const ErrorList = styled.ul(({ theme }) => ({
  marginTop: "1rem",
  marginBottom: "2rem",
  color: theme.colors.primary,
}));

export type { FilterErrors };
export { SearchFilters };
